import { HotelReserveCardDetailCancellation } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-cancellation";
import { useHotelReserveCardDetail } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-detail/hotel-reserve-card-detail.hook";
import { HotelReservedCardDetailPropertiesType } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-detail/hotel-reserve-card-detail.types";
import { HotelReserveCardDetailFacilities } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-facilities";
import { HotelReserveCardFooter } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-reserve-card-footer";
import { HotelRoomCalendar } from "@website/accommodation-ui/hotel-reserve-rooms-layout/hotel-reserve-card/hotel-room-calendar";
import { MaterialIcon } from "@website/icons/material";
import { Button } from "@website/ui/button";
import { Loading } from "@website/ui/loading";
import { Modal } from "@website/ui/modal";
import { Skeleton } from "@website/ui/skeleton";
import { deepCompare } from "@website/utils/deep-compare";
import { isFilled } from "@website/utils/is-filled";
import { ReserveCommentPopupScroll } from "@website/widget/reserve-comment-popup-scroll";
import omit from "lodash-es/omit";
import { useRouter } from "next/router";
import { memo, useCallback } from "react";

const Component = (properties: HotelReservedCardDetailPropertiesType) => {
  const { title, roomDetail, availableRooms, hotelId, cancellation_policy } =
    properties;
  const {
    menuItems,
    onClick,
    isLoading,
    hotelRoomCalendar,
    hotelReserveCardDetailComments,
    hotelReserveCardFooterProperties,
    hotelReserveCardDetailFacilitiesProperties
  } = useHotelReserveCardDetail(properties);
  const router = useRouter();

  const onClose = useCallback(
    (isIcon: boolean) => {
      isIcon && roomDetail?.setShowRoomDetail(false);
      if (roomDetail?.onLoadFilter && isFilled(roomDetail?.filters)) {
        roomDetail?.onLoadFilter(roomDetail?.filters);
      }
      return router.push(
        {
          pathname: router.pathname,
          query: {
            ...omit(
              router?.query,
              "popover",
              Object.keys(router?.query).filter((item) =>
                item.startsWith("filter_")
              )
            )
          }
        },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [roomDetail, router]
  );

  return (
    <Modal
      className="shadow-4 !fixed right-0 !z-[29] !h-full !rounded-none !p-0 md:left-0 md:right-auto md:!w-[466px] lg:!w-[480px]"
      onClose={() => onClose(false)}
      open={roomDetail?.showRoomDetail ?? false}
    >
      <div className="flex h-full flex-col">
        <div className="text-subtitle-1 flex flex-row items-center justify-start gap-4 px-6 py-4">
          <MaterialIcon
            className="text-on-surface-medium-emphasis scale-125 cursor-pointer"
            name="arrow_forward"
            onClick={() => onClose(true)}
          />
          <span className="truncate">{title}</span>
        </div>
        <div className="flex flex-row gap-8 border-y px-6">
          {menuItems?.map((item) => (
            <div
              className={`text-body-2 cursor-pointer whitespace-nowrap py-4 ${
                item.selected
                  ? "text-secondary border-secondary border-b-2"
                  : "text-on-surface-high-emphasis"
              }`}
              id={item.id}
              key={item.id}
              onClick={(event) => onClick(event)}
            >
              {item.title}
            </div>
          ))}
        </div>
        <div className="h-full overflow-y-auto border-b">
          {menuItems?.map((item) => {
            if (item.id === "cancellation" && item.selected)
              return (
                <HotelReserveCardDetailCancellation
                  hotelId={hotelId}
                  roomId={roomDetail?.roomId}
                  cancellation_policy={cancellation_policy}
                />
              );
            if (item.id === "facilities" && item.selected)
              return (
                <HotelReserveCardDetailFacilities
                  {...hotelReserveCardDetailFacilitiesProperties}
                />
              );
            if (item.id === "roomCalendar" && item.selected)
              return isLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Loading />
                </div>
              ) : (
                isFilled(hotelRoomCalendar.calendar) && (
                  <HotelRoomCalendar {...hotelRoomCalendar} />
                )
              );
            if (item.id === "comments" && item.selected)
              return isFilled(roomDetail?.filters) &&
                router?.query["filter_attitude"] &&
                !isFilled(roomDetail?.comments) ? (
                <ReserveCommentPopupScroll
                  {...hotelReserveCardDetailComments}
                  className="h-full"
                />
              ) : isFilled(roomDetail?.comments) ? (
                <ReserveCommentPopupScroll
                  {...hotelReserveCardDetailComments}
                  className="h-full"
                />
              ) : null;
            return null;
          })}
        </div>
        <div className="mt-auto">
          {isLoading || roomDetail?.hotelRoomsFetching ? (
            <div className="flex h-[118px] flex-col gap-2 p-4">
              <Skeleton height={40} />
              <div className="flex flex-row justify-between">
                <div className="w-[133px]">
                  <Skeleton height={40} shape="rectangle" />
                </div>
                <Button className="w-[133px]" disabled text="رزرو اتاق" />
              </div>
            </div>
          ) : (
            availableRooms && (
              <HotelReserveCardFooter
                {...hotelReserveCardFooterProperties}
                onModalClose={onClose}
              />
            )
          )}
        </div>
      </div>
    </Modal>
  );
};
export const HotelReserveCardDetail = memo(Component, deepCompare);
